"use client";

import React from "react";
import { MoonStar, BatteryWarning, Brain, Activity, ShieldAlert, HeartPulse } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const symptoms = [{
  id: 1,
  title: "Persistent Snoring",
  description: "Loud, frequent snoring is more than just a nuisance; it is often the most common indicator of a partially obstructed airway during sleep.",
  icon: MoonStar
}, {
  id: 2,
  title: "Daytime Fatigue",
  description: "Feeling overwhelmingly exhausted despite a full night's rest can indicate poor sleep quality caused by frequent breathing interruptions.",
  icon: BatteryWarning
}, {
  id: 3,
  title: "Morning Headaches",
  description: "Waking up with a dull, throbbing headache is a frequent result of fluctuating oxygen levels and carbon dioxide buildup overnight.",
  icon: Brain
}, {
  id: 4,
  title: "Gasping for Air",
  description: "Episodes of choking, snorting, or gasping for breath during sleep are clear warning signs that your breathing is being compromised.",
  icon: Activity
}, {
  id: 5,
  title: "Fragmented Sleep",
  description: "Frequent awakenings and restless tossing often stem from the body's survival mechanism to restart breathing after an apnea event.",
  icon: ShieldAlert
}, {
  id: 6,
  title: "Mood & Memory",
  description: "Untreated sleep apnea often leads to irritability, depression, and difficulty concentrating, commonly referred to as 'brain fog.'",
  icon: HeartPulse
}];
export default function TheSleepGap() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="99171" id="the-silent-warning-signs" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[70%] w-[50%] animate-pulse rounded-full bg-primary/5 blur-[120px] duration-[8000ms]" />
        <div className="absolute top-[40%] -right-[20%] h-[80%] w-[60%] animate-pulse rounded-full bg-secondary/5 blur-[150px] duration-[12000ms]" />
        <div className="absolute -bottom-[30%] left-[20%] h-[60%] w-[50%] animate-pulse rounded-full bg-primary/10 blur-[100px] duration-[10000ms]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="font-sans text-4xl font-bold tracking-tight text-primary sm:text-5xl md:text-6xl">
            The Silent Warning Signs
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-relaxed md:text-xl">
            Millions of adults live with undiagnosed sleep apnea. Recognizing these common symptoms is the first step toward reclaiming your health and achieving restorative rest.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {symptoms.map((symptom, i) => {
          const Icon = symptom.icon;
          return <div data-index={i} key={symptom.id} className="group h-full">
                <Card className="bg-card border-border hover:border-primary/40 hover:shadow-xl relative h-full overflow-hidden backdrop-blur-md transition-all duration-500">
                  {}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  
                  <CardHeader className="pb-4">
                    <div className="bg-primary/10 text-primary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 group-hover:bg-primary/20">
                      <Icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl tracking-tight text-primary">
                      {symptom.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed">
                      {symptom.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button size="lg" className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg shadow-md transition-all hover:shadow-lg">
            Start Your Free Assessment
          </Button>
        </div>
      </div>
    </section>;
}